import { Spin } from "antd";
import i18n from "i18next";
import type { FC } from "react";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import type { LoyaltyAllowedMatrixEntityList } from "src/app/features/LoyaltyAllowedMatrix/types/LoyaltyAllowedMatrixEntityList";
import { FormManager } from "../../../../../components/Form/FormManager";
import { useLoadFormDefinitionQuery } from "../../../../../components/Form/services/server/serverApi";
import { FormLayoutType } from "../../../../../components/Form/types";
import type { FormProps } from "../../../../../components/Form/types/FormProps";
import { showSuccessNotification } from "../../../../../core/setup/redux/state/notifications/notificationsState";
import { useLoyaltyAllowedMatrixLoadQuery, useLoyaltyAllowedMatrixUpdateMutation } from "../../services/server/LoyaltyAllowedMatrixApi";

export const LoyaltyAllowedMatrixEditForm: FC<FormProps> = ({ runSubmit }) => {
    const { data: formDefinition, isFetching: configIsFetching } = useLoadFormDefinitionQuery("loyalty-allowed-matrix");

    const [formData, setFormData] = useState<LoyaltyAllowedMatrixEntityList>({} as LoyaltyAllowedMatrixEntityList);
    const { data: loadFormData, isFetching: loadFormIsInProgress } = useLoyaltyAllowedMatrixLoadQuery();

    useEffect(() => {
        if (loadFormData) {
            setFormData(loadFormData);
        }
    }, [loadFormData, setFormData]);
    const onChangeCallback = useCallback(setFormData, [setFormData]);

    const [saveFormData, { isLoading: saveFormIsInProgress }] = useLoyaltyAllowedMatrixUpdateMutation();
    const dispatch = useDispatch();
    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formData) {
                return;
            }

            const result = await saveFormData({ data: formData });

            const hasError = "error" in result;
            if (!hasError) {
                dispatch(
                    showSuccessNotification({
                        text: i18n.t("common.formUpdateSuccessNoId", {
                            resource: i18n.t("menu.loyaltyAllowedMatrix"),
                        }),
                        autoCloseDisabled: false,
                    }),
                );
            }
        },
        [saveFormData, formData, dispatch],
    );

    return (
        <Spin spinning={saveFormIsInProgress || configIsFetching || loadFormIsInProgress}>
            {formDefinition?.formConfiguration && (
                <FormManager<LoyaltyAllowedMatrixEntityList>
                    name="LoyaltyAllowedMatrixEditForm"
                    formData={formData}
                    formLayout={FormLayoutType.DEFAULT}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
        </Spin>
    );
};
